import { UserProfile, WardrobeItem, SavedContent, SavedOutfit, Lookbook, MembershipLevel, SavedStyleItem } from '../types';
import { Logger } from './logger';
import { apiClient, isExternalBackend } from './apiClient';
import { MEMBERSHIP_LIMITS, STYLE_AGENT_LIMITS } from '@shared/constants';
import { FIREBASE_CONFIG } from '@shared/config';
import { getDocumentFromFirestore, saveDocumentToFirestore } from './gcpStorageService';

export class BaseUserService {
    protected storagePrefix: string;

    constructor(storagePrefix: string = 'virtual_try_on_user_') {
        this.storagePrefix = storagePrefix;
    }

    protected getStorageKey(userId: string, key: string): string {
        return `${this.storagePrefix}${userId}_${key}`;
    }

    public async loadData<T>(userId: string, key: string, defaultValue: T): Promise<T> {
        // 1. Try Firestore if enabled
        if (FIREBASE_CONFIG.USE_FIREBASE_CONFIG_STORAGE) {
            try {
                const firestoreCollection = `users/${userId}/${key}`;
                // Note: Firestore data is usually stored at the root of a document. 
                // For nested data like 'profile', we use a specific document ID.
                const cloudData = await getDocumentFromFirestore(key === 'profile' ? 'user_profiles' : key, userId);
                if (cloudData) {
                    // Update cache
                    localStorage.setItem(this.getStorageKey(userId, key), JSON.stringify(cloudData));
                    return cloudData as T;
                }
            } catch (e) {
                Logger.error(`Firestore fetch failed for ${key} of user ${userId}`, e);
            }
        }

        // 2. Fallback to LocalStorage
        try {
            const data = localStorage.getItem(this.getStorageKey(userId, key));
            return data ? JSON.parse(data) : defaultValue;
        } catch (e) {
            Logger.error(`Failed to load ${key} for user ${userId}`, e);
            return defaultValue;
        }
    }

    public async saveData<T>(userId: string, key: string, data: T): Promise<void> {
        // 1. Save to LocalStorage always (as cache/fallback)
        try {
            localStorage.setItem(this.getStorageKey(userId, key), JSON.stringify(data));
        } catch (e) {
            Logger.error(`Failed to save ${key} to localStorage for user ${userId}`, e);
            if (e instanceof DOMException && e.name === 'QuotaExceededError') {
                throw new Error(`error_storage_full_${key}`);
            }
        }

        // 2. Sync to Firestore if enabled
        if (FIREBASE_CONFIG.USE_FIREBASE_CONFIG_STORAGE) {
            try {
                await saveDocumentToFirestore(key === 'profile' ? 'user_profiles' : key, userId, data);
            } catch (e) {
                Logger.error(`Firestore sync failed for ${key} of user ${userId}`, e);
            }
        }
    }

    // --- User Profile ---
    public async getProfile(userId: string): Promise<UserProfile | null> {
        if (isExternalBackend()) {
            try {
                const profile = await apiClient.get<UserProfile>(`/api/users/${userId}/profile`);
                if (profile) {
                    await this.saveProfile(userId, profile);
                    return profile;
                }
            } catch (e) {
                Logger.error(`[API] Failed to fetch profile for ${userId}`, e);
            }
        }
        return this.loadData<UserProfile | null>(userId, 'profile', null);
    }

    public async saveProfile(userId: string, profile: UserProfile): Promise<void> {
        await this.saveData(userId, 'profile', profile);
        if (isExternalBackend()) {
            apiClient.post(`/api/users/${userId}/profile`, profile).catch(e =>
                Logger.error(`[API] Failed to sync profile for ${userId}`, e)
            );
        }
    }

    public async updateProfile(userId: string, profile: UserProfile): Promise<boolean> {
        await this.saveProfile(userId, profile);
        return true;
    }

    public async updateAvatar(userId: string, avatar: string): Promise<boolean> {
        const profile = await this.getProfile(userId);
        if (profile) {
            await this.saveProfile(userId, { ...profile, avatar });
            return true;
        }
        return false;
    }

    public async updateMembership(userId: string, level: string | MembershipLevel): Promise<boolean> {
        const profile = await this.getProfile(userId);
        if (profile) {
            await this.saveProfile(userId, { ...profile, membershipLevel: level });
            return true;
        }
        return false;
    }

    public async addSavedStyle(userId: string, styleItem: SavedStyleItem): Promise<SavedStyleItem[] | null> {
        const profile = await this.getProfile(userId);
        if (profile) {
            const currentStyles = profile.savedStyles || [];
            const exists = currentStyles.some((s: any) => s.value === styleItem.value && s.type === styleItem.type);
            if (exists) return currentStyles;
            const newStyles = [styleItem, ...currentStyles];
            await this.saveProfile(userId, { ...profile, savedStyles: newStyles });
            return newStyles;
        }
        return null;
    }

    public async removeSavedStyle(userId: string, styleId: string): Promise<SavedStyleItem[] | null> {
        const profile = await this.getProfile(userId);
        if (profile) {
            const currentStyles = profile.savedStyles || [];
            const newStyles = currentStyles.filter((s: any) => s.id !== styleId);
            await this.saveProfile(userId, { ...profile, savedStyles: newStyles });
            return newStyles;
        }
        return null;
    }

    public async completeOnboarding(userId: string): Promise<boolean> {
        const profile = await this.getProfile(userId);
        if (profile) {
            await this.saveProfile(userId, { ...profile, onboardingCompleted: true });
            return true;
        }
        return false;
    }

    public async trackWear(userId: string, itemId: string): Promise<boolean> {
        const wardrobe = await getUserWardrobe(userId);
        const itemIndex = wardrobe.findIndex(i => i.id === itemId);
        if (itemIndex !== -1) {
            wardrobe[itemIndex].usageCount = (wardrobe[itemIndex].usageCount || 0) + 1;
            wardrobe[itemIndex].lastWorn = Date.now();
            await saveUserWardrobe(userId, wardrobe);
            return true;
        }
        return false;
    }
}

export const userServiceInstance = new BaseUserService();
export const userService = userServiceInstance;

// --- Exported standalone functions for backward compatibility ---

export const getUserProfile = async (userId: string): Promise<UserProfile> => {
    const isDefaultAdmin = userId.toLowerCase().includes('admin');
    const defaultProfile: any = {
        measurements: {},
        membershipLevel: isDefaultAdmin ? 'advanced' : 'basic',
        saveImageQuality: 'medium',
        connectedAccounts: {},
        generationCount: 0,
        generationLimitResetDate: new Date().toISOString(),
        favoritePoses: []
    };

    const profile = await userServiceInstance.getProfile(userId) || defaultProfile;

    if (isDefaultAdmin) {
        profile.membershipLevel = 'advanced';
        profile.role = 'ADMIN';
    } else if (!profile.membershipLevel) {
        profile.membershipLevel = 'basic';
    }

    if (!profile.saveImageQuality) profile.saveImageQuality = 'medium';
    if (profile.generationCount === undefined) profile.generationCount = 0;
    if (!profile.favoritePoses) profile.favoritePoses = [];
    if (!profile.accountStatus) profile.accountStatus = 'active';

    return profile;
};

export const saveUserProfile = async (userId: string, profile: UserProfile): Promise<void> => {
    await userServiceInstance.saveProfile(userId, profile);
};

export const getUserWardrobe = async (userId: string): Promise<WardrobeItem[]> => {
    return await userServiceInstance.loadData<WardrobeItem[]>(userId, 'wardrobe', []);
};

export const saveUserWardrobe = async (userId: string, wardrobe: WardrobeItem[]): Promise<void> => {
    await userServiceInstance.saveData(userId, 'wardrobe', wardrobe);
};

// --- Access Control & Quota ---
export const hasMembershipAccess = (profile: UserProfile): boolean => {
    if (profile.accountStatus !== 'active') return false;
    if (!profile.membershipExpiry) {
        if (profile.membershipLevel === 'one_time') return false;
        return true;
    }
    const now = new Date();
    const expiryDate = profile.membershipExpiry.includes('T')
        ? new Date(profile.membershipExpiry)
        : new Date(profile.membershipExpiry + 'T23:59:59');
    return now < expiryDate;
};

export const hasAssistantAccess = (profile: UserProfile): boolean => {
    if (!profile) return false;
    const level = profile.membershipLevel || 'basic';
    const allowedTiers = ['premium', 'advanced', 'nextgen', 'one_time'];
    return allowedTiers.includes(level) && hasMembershipAccess(profile);
};

export const hasFeatureAccess = (profile: UserProfile, featureId?: string, requiredLevel?: string): boolean => {
    if (profile.role === 'ADMIN') return true;
    const membershipLevel = profile.membershipLevel || 'basic';
    const levelValue: any = { 'free': 0, 'basic': 1, 'premium': 2, 'advanced': 3, 'nextgen': 4 };

    // 1. Level check
    if (requiredLevel && (levelValue[membershipLevel as string] || 0) >= (levelValue[requiredLevel] || 0)) {
        return true;
    }

    // 2. Feature check
    if (featureId) {
        const reqs: any = { 'wardrobe_3d': 'advanced', 'runway_video': 'advanced', 'style_report': 'premium', 'magazine_lookbook': 'premium', 'color_analysis': 'premium', 'color_report': 'premium', 'capsule_generator': 'premium' };
        if (reqs[featureId] && (levelValue[membershipLevel as string] || 0) >= (levelValue[reqs[featureId]] || 0)) return true;

        const purchased = profile.purchasedFeatures?.[featureId];
        if (purchased && ((purchased.expiry && new Date() < new Date(purchased.expiry)) || (purchased.remainingAmount || 0) > 0)) return true;
    }
    return false;
};

export const consumeOnDemandFeature = async (userId: string, featureId: string): Promise<UserProfile> => {
    const profile = await getUserProfile(userId);
    const purchased = profile.purchasedFeatures?.[featureId];
    if (purchased && purchased.remainingAmount !== undefined && purchased.remainingAmount > 0) {
        const updated = {
            ...profile,
            purchasedFeatures: {
                ...profile.purchasedFeatures,
                [featureId]: { ...purchased, remainingAmount: purchased.remainingAmount - 1 }
            }
        };
        await saveUserProfile(userId, updated);
        return updated;
    }
    return profile;
};

export const checkGenerationQuota = (profile: UserProfile, userId?: string, featureId?: string): boolean => {
    if (featureId && profile.purchasedFeatures?.[featureId]?.remainingAmount !== undefined && profile.purchasedFeatures[featureId].remainingAmount! > 0) return true;
    if (userId?.includes('guest')) {
        const consumed = localStorage.getItem('onit_guest_generation_consumed') === 'true';
        return !consumed && (profile.generationCount || 0) < 1;
    }
    const level = (hasMembershipAccess(profile) ? (profile.membershipLevel || 'basic') : 'basic') as keyof typeof MEMBERSHIP_LIMITS;
    const limit = (MEMBERSHIP_LIMITS as any)[level] || (MEMBERSHIP_LIMITS as any)['basic'];
    return (profile.generationCount || 0) < limit;
};

export const incrementGenerationCount = async (userId: string): Promise<UserProfile> => {
    const profile = await getUserProfile(userId);
    const newCount = (profile.generationCount || 0) + 1;
    if (userId.includes('guest')) localStorage.setItem('onit_guest_generation_consumed', 'true');
    const updated = { ...profile, generationCount: newCount };
    await saveUserProfile(userId, updated);
    return updated;
};

export const checkStyleAgentQuota = (profile: UserProfile): boolean => {
    const level = profile.membershipLevel || 'basic';
    const limit = (STYLE_AGENT_LIMITS as any)[level] || 0;
    const currentCount = profile.styleAgents?.length || 0;
    return currentCount < limit;
};

export const saveUserSavedOutfits = async (userId: string, outfits: SavedOutfit[]): Promise<void> => {
    try {
        localStorage.setItem(`virtual_try_on_user_${userId}_outfits`, JSON.stringify(outfits));
        if (isExternalBackend()) {
            await apiClient.post(`/api/users/${userId}/outfits`, outfits).catch(e =>
                Logger.error(`[API] Failed to sync outfits for ${userId}`, e)
            );
        }
    } catch (e) {
        Logger.error(`Failed to save outfits for user ${userId}`, e);
    }
};

export const saveUserLookbooks = (userId: string, lookbooks: Lookbook[]): void => {
    try {
        localStorage.setItem(`virtual_try_on_user_${userId}_lookbooks`, JSON.stringify(lookbooks));
        if (isExternalBackend()) {
            apiClient.post(`/api/users/${userId}/lookbooks`, lookbooks).catch(e =>
                Logger.error(`[API] Failed to sync lookbooks for ${userId}`, e)
            );
        }
    } catch (e) {
        Logger.error(`Failed to save lookbooks for user ${userId}`, e);
    }
};

export const toggleUserFavoritePose = async (userId: string, poseInstruction: string): Promise<UserProfile> => {
    const profile = await getUserProfile(userId);
    const currentFavorites = new Set(profile.favoritePoses || []);
    if (currentFavorites.has(poseInstruction)) currentFavorites.delete(poseInstruction);
    else currentFavorites.add(poseInstruction);
    const updatedProfile = { ...profile, favoritePoses: Array.from(currentFavorites) };
    await saveUserProfile(userId, updatedProfile);
    return updatedProfile;
};

export const getUserSavedContent = async (userId: string): Promise<SavedContent[]> => {
    return await userServiceInstance.loadData<SavedContent[]>(userId, 'saved_content', []);
};

export const saveUserSavedContent = async (userId: string, content: SavedContent[]): Promise<void> => {
    await userServiceInstance.saveData(userId, 'saved_content', content);
};

export const deleteUserData = (userId: string): void => {
    const keys = ['outfits', 'lookbooks', 'profile', 'favorites', 'saved_content', 'wardrobe'];
    keys.forEach(key => localStorage.removeItem(`virtual_try_on_user_${userId}_${key}`));
    if (isExternalBackend()) {
        apiClient.delete(`/api/users/${userId}`).catch(e =>
            Logger.error(`[API] Failed to delete user data for ${userId}`, e)
        );
    }
};

export const getAllUserIds = (): string[] => {
    const ids: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('virtual_try_on_user_') && key.endsWith('_profile')) {
            const parts = key.split('_');
            if (parts.length >= 5) ids.push(parts[4]);
        }
    }
    return ids;
};

export const getAllUserProfiles = async (): Promise<{ userId: string, profile: UserProfile }[]> => {
    const ids = getAllUserIds();
    const profiles = await Promise.all(ids.map(async (userId) => ({
        userId,
        profile: await getUserProfile(userId)
    })));
    return profiles;
};

export const isEmailRegistered = async (email: string): Promise<boolean> => {
    const profiles = await getAllUserProfiles();
    return profiles.some(p => p.profile.email?.toLowerCase() === email.toLowerCase());
};

export const updateUserStylePreferences = async (userId: string, tags: string[], action: 'like' | 'dislike'): Promise<UserProfile> => {
    const profile = await getUserProfile(userId);
    const currentPreferences = profile.stylePreferences || { likedTags: [], dislikedTags: [] };
    const targetArray = action === 'like' ? currentPreferences.likedTags : currentPreferences.dislikedTags;
    const otherArray = action === 'like' ? currentPreferences.dislikedTags : currentPreferences.likedTags;
    const updatedTarget = Array.from(new Set([...targetArray, ...tags]));
    const updatedOther = otherArray.filter(tag => !tags.includes(tag));
    const updatedProfile: UserProfile = {
        ...profile,
        stylePreferences: {
            likedTags: action === 'like' ? updatedTarget : updatedOther,
            dislikedTags: action === 'dislike' ? updatedTarget : updatedOther
        }
    };
    await saveUserProfile(userId, updatedProfile);
    return updatedProfile;
};

export const getUserSavedOutfits = async (userId: string): Promise<SavedOutfit[]> => {
    return await userServiceInstance.loadData<SavedOutfit[]>(userId, 'outfits', []);
};

export const getUserFavorites = async (userId: string): Promise<string[]> => {
    return await userServiceInstance.loadData<string[]>(userId, 'favorites', []);
};

export const saveUserFavorites = async (userId: string, favoriteIds: string[]): Promise<void> => {
    await userServiceInstance.saveData(userId, 'favorites', favoriteIds);
};

export const getUserLookbooks = async (userId: string): Promise<Lookbook[]> => {
    return await userServiceInstance.loadData<Lookbook[]>(userId, 'lookbooks', []);
};

export const updateAvatar = (userId: string, avatar: string): Promise<boolean> =>
    userService.updateAvatar(userId, avatar);

export const updateMembership = (userId: string, level: string | MembershipLevel): Promise<boolean> =>
    userService.updateMembership(userId, level);

export const addSavedStyle = (userId: string, styleItem: SavedStyleItem): Promise<SavedStyleItem[] | null> =>
    userService.addSavedStyle(userId, styleItem);

export const removeSavedStyle = (userId: string, styleId: string): Promise<SavedStyleItem[] | null> =>
    userService.removeSavedStyle(userId, styleId);

export const completeOnboarding = (userId: string): Promise<boolean> =>
    userService.completeOnboarding(userId);

export const trackWear = (userId: string, itemId: string): Promise<boolean> =>
    userService.trackWear(userId, itemId);
